/**
 * VP8 Data Extractor for LiveKit.
 *
 * Extracts animation data from VP8 video tracks using RTCRtpScriptTransform.
 *
 * @internal
 * @packageDocumentation
 */

import type { AnimationTrackCallbacks } from '../../core/types';
import type { AnimationTransformEvent } from './types';
import { createAnimationReceiverTransform } from './animation-transform';
import { logger, trackEvent } from '../../utils';

// Import worker with inline - this bundles the worker code as a blob URL
import AnimationWorker from './animation-worker.ts?worker&inline';

// Default transition frame counts (used when protocol doesn't specify)
const DEFAULT_TRANSITION_START_FRAMES = 8;
const DEFAULT_TRANSITION_END_FRAMES = 12;

/**
 * VP8Extractor - Extracts animation data from VP8 video tracks.
 *
 * Uses RTCRtpScriptTransform and a Web Worker to parse VP8 frames
 * and extract embedded animation data.
 *
 * @internal
 */
export class VP8Extractor {
  /** @internal */
  private callbacks: AnimationTrackCallbacks | null = null;
  /** @internal */
  private receiver: RTCRtpReceiver | null = null;
  /** @internal */
  private transform: RTCRtpScriptTransform | null = null;

  /**
   * Initialize the extractor with a receiver and callbacks.
   * @param receiver - RTCRtpReceiver to extract data from
   * @param callbacks - Callbacks to receive extracted data
   * @internal
   */
  async initialize(
    receiver: RTCRtpReceiver,
    callbacks: AnimationTrackCallbacks,
  ): Promise<void> {
    if (this.receiver || this.transform) {
      throw new Error('VP8Extractor already initialized');
    }

    this.receiver = receiver;
    this.callbacks = callbacks;

    // Check if transform is already set
    if (receiver.transform) {
      return;
    }

    // Create worker and transform
    const worker = new AnimationWorker();
    const onEvent = (evt: AnimationTransformEvent) => {
      this.handleTransformEvent(evt);
    };

    this.transform = createAnimationReceiverTransform(worker, onEvent);
    receiver.transform = this.transform;
  }

  /**
   * Handle events from the animation transform.
   * @internal
   */
  private handleTransformEvent(evt: AnimationTransformEvent): void {
    if (!this.callbacks) return;

    if (evt.type === 'metadata') {
      this.callbacks.onStreamStats({
        framesPerSec: evt.framesPerSec,
        totalFrames: evt.totalFrames ?? 0,
        framesSent: evt.framesSent ?? 0,
        framesLost: evt.framesLost ?? 0,
        framesRecovered: evt.framesRecovered ?? 0,
        framesDropped: evt.framesDropped ?? 0,
        framesOutOfOrder: evt.framesOutOfOrder ?? 0,
        framesDuplicate: evt.framesDuplicate ?? 0,
        lastRenderedSeq: evt.lastRenderedSeq ?? -1,
      });
    } else if (evt.type === 'transition') {
      this.callbacks.onTransition(
        evt.protobufData,
        DEFAULT_TRANSITION_START_FRAMES,
      );
    } else if (evt.type === 'transitionEnd') {
      this.callbacks.onTransitionEnd(
        evt.protobufData,
        DEFAULT_TRANSITION_END_FRAMES,
      );
    } else if (evt.type === 'animation') {
      if (!evt.isIdle) {
        this.callbacks.onAnimationData(evt.protobufData, {
          frameSeq: evt.frameSeq,
          isStart: evt.isStart,
          isEnd: evt.isEnd,
          isIdle: evt.isIdle,
          isRecovered: evt.isRecovered,
        });
      }
    } else if (evt.type === 'idleStart') {
      this.callbacks.onIdleStart();
    } else if (evt.type === 'error') {
      logger.error('VP8Extractor', 'Error:', evt.error);
      trackEvent('rtc_worker_failed', 'error', {
        provider: 'livekit',
        description: typeof evt.error === 'string' ? evt.error : String(evt.error),
      });
    }
  }

  /**
   * Check if this extractor is connected to the given receiver.
   * @param receiver - RTCRtpReceiver to check
   * @returns true if connected to this receiver
   * @internal
   */
  isConnectedTo(receiver: RTCRtpReceiver): boolean {
    return this.receiver === receiver;
  }

  /**
   * Get the receiver this extractor is connected to.
   * @returns The connected RTCRtpReceiver or null
   * @internal
   */
  getReceiver(): RTCRtpReceiver | null {
    return this.receiver;
  }

  /**
   * Dispose the extractor and release resources.
   * @internal
   */
  dispose(): void {
    this.receiver = null;
    this.transform = null;
    this.callbacks = null;
  }
}
